import { Button, Container, Dialog, Stack, TextField, SxProps, Typography, Box, Divider, Collapse, FormControlLabel, Radio, RadioGroup } from '@mui/material'
import React, { FC, useCallback, useEffect, useState } from 'react'
import { btnPrimary, btnSecondary, containerStyles } from '../../pages/main/profile/ModalAddress/styles'
import { useModal } from '../../contexts/modal'
import MCConfirm, { h2Style } from './MCConfirm'
import palette from '../../theme/palette'
import { stackFlexColumn, stackFlexRow } from '../../utils/MUIShortcuts'
import * as Yup from 'yup'
import { useFormik } from 'formik'
import { applyMask, exists, objectMap, removeEmptyAttributes } from '../../utils'
import MCAlert from './MCAlert'
import { useAuth } from '../../contexts/auth2'
import { handlePromise } from '../../utils/handlePromisse'
import { getUserProfile } from '../../services/profileService'
import { useNavigate } from 'react-router'
import { isValidCPF } from "../../utils/Validator";
import PlanModel from '../../models/plan.model'
import { CouponVerifyResponse, verifyCoupon } from '../../services/coupon/coupon.services'
import { SimpleSnackbar } from '../snackbar'
import styled from 'styled-components'
import { formatCurrency } from "../../utils/formatNumber";
import Space from "../Space";

export type OrderInfoState = {
  titularName: string,
  birthdate: string,
  paymentCpf: string,
  invoiceWithCpf: '' | 'yes' | 'no',
  invoiceCpfOption: '' | 'same' | 'other',
  invoiceCpf: string,
  expirationDate: string,
  cvv: string,
  cardNumber: string,
  brand: string,
}

const helperTextStyle: any = {
  style: {
    background: 'white',
    margin: '0px',
    padding: '4px 16px 0px 16px'
  }
}

const CouponText = styled(Typography)`
  && {
    color: ${palette.secondary.main};
    line-height: 1.5;
  }
`;

const CouponTextBold = styled(Typography)`
  && {
    color: ${palette.secondary.main};
    font-size: 0.950rem;
    font-weight: bold;
    line-height: 1.5;
  }
`;

const CouponBox = styled(Box)`
  && {
    display: flex;
    justify-content: space-between;
  }
`;

const StepLabel = styled(Typography)`
  && {
    color: ${palette.primary.dark};
    font-size: 0.78rem;
    font-weight: 700;
    letter-spacing: 0.4px;
    margin-top: 8px;
    margin-bottom: 6px;
    text-align: center;
    text-transform: uppercase;
  }
`;

const PaymentOptionCard = styled(Box)<{ selected: boolean }>`
  && {
    width: 100%;
    border: 1px solid ${({ selected }) => selected ? palette.secondary.main : palette.primary.light};
    background: ${({ selected }) => selected ? '#F7F1EA' : '#ffffff'};
    border-radius: 4px;
    padding: 2px 8px;
    margin-bottom: 8px;
  }
`;

export const btnCoupon: SxProps = {
  background: 'white',
}

type MCOrderInfoProps = {
  plan: PlanModel;
  onClose?: () => void
  onAssign?: (values: OrderInfoState, couponCode?: string) => void,
  preValues?: OrderInfoState,
}

const MCOrderInfo: FC<MCOrderInfoProps> = ({ onClose, onAssign, preValues, plan }) => {
  const { setModalOpen, setModalContent } = useModal()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [isThereAddress, setIsThereAddress] = useState(false)
  const [coupon, setCoupon] = useState('')
  const [showSnackbar, setShowSnackbar] = useState(false)
  const [snackbarMessage, setSnackbarMessage] = useState('')
  const [couponInfo, setCouponInfo] = useState<CouponVerifyResponse|null>();
  // Código que passou pela validação: só ele pode ir para a API (a API recusa o
  // pedido inteiro com "Cupom inválido" se receber um código que não vale).
  const [validatedCoupon, setValidatedCoupon] = useState('')
  // Mensagem exibida no próprio campo quando o cupom não vale: a compra segue
  // sem desconto, então o aviso precisa ficar visível ali e não só num snackbar.
  const [couponError, setCouponError] = useState('')
  // Código que já foi reprovado: evita chamar o /coupon/verify de novo (e o
  // segundo alerta) quando a pessoa clica em Continuar com o mesmo cupom.
  const [failedCoupon, setFailedCoupon] = useState('')

  useEffect(() => {
    (async () => {
      const [result, error] = await handlePromise(getUserProfile(user.idUser))
      if (error) {
        setModalContent(<MCAlert title="Erro ao buscar informações do usuário"/>)
      }
      if (!!result && !!result.data) {
        const addressLength = Object.entries(removeEmptyAttributes(result.data.address)).length
        const isThereAddress = exists(result.data.address) || addressLength >= 6

        setIsThereAddress(isThereAddress);
        if (!isThereAddress) {
          setModalContent(<MCConfirm
            title="Perfil incompleto!"
            text="Para aderir um plano é necessário completar suas informações."
            confirmButtonText="completar"
            cancelButtonText="agora não"
            onConfirm={() => {
              navigate('/dashboard/profile')
              setModalOpen(false)
            }}
          />)
          setModalOpen(true)
        }
      }
    })()
  }, [user.idUser, setModalContent, setModalOpen, navigate])

  const schema = Yup.object().shape({
    paymentCpf: Yup.string()
      .required('Este campo é obrigatório')
      .transform((val) => (val || '').replace(/\D/g, ''))
      .min(11, 'Digite ao menos 11 dígitos'),
    invoiceWithCpf: Yup.string()
      .oneOf(['yes', 'no'], 'Selecione uma opção')
      .required('Selecione uma opção'),
    invoiceCpfOption: Yup.string().when('invoiceWithCpf', {
      is: 'yes',
      then: (field) => field.oneOf(['same', 'other'], 'Selecione uma opção').required('Selecione uma opção'),
      otherwise: (field) => field.notRequired(),
    }),
    invoiceCpf: Yup.string().when(['invoiceWithCpf', 'invoiceCpfOption'], {
      is: (invoiceWithCpf: string, invoiceCpfOption: string) => invoiceWithCpf === 'yes' && invoiceCpfOption === 'other',
      then: (field) => field.required('Este campo é obrigatório').transform((val) => (val || '').replace(/\D/g, '')).min(11, 'Digite ao menos 11 dígitos'),
      otherwise: (field) => field.notRequired(),
    }),
  })

  const { errors, touched, getFieldProps, values, setValues, validateForm, setTouched } = useFormik<OrderInfoState>({
    initialValues:
      preValues ?
        preValues :
        {
          titularName: '',
          birthdate: '',
          paymentCpf: '',
          invoiceWithCpf: '',
          invoiceCpfOption: '',
          invoiceCpf: '',
          expirationDate: '',
          cvv: '',
          brand: '',
          cardNumber: ''
        },
    validationSchema: schema,
    onSubmit: (values) => {
    },
    validateOnChange: true,
    validateOnBlur: true
  })

  const onCloseCallback = useCallback(() => {
    if (!!onClose) {
      onClose()
      setModalOpen(false)
    } else {
      setModalOpen(false)
    }
  }, [onClose, setModalOpen])

  const alertAndRerender = useCallback((title: string, text: string) => {
    setModalContent(
      <MCAlert
        title={title}
        text={text}
        onClose={
          () => setModalContent(
            <MCOrderInfo
              plan={plan}
              onClose={onClose}
              onAssign={onAssign}
              preValues={values}
            />
          )
        }
      />
    )
  }, [onClose, onAssign, setModalContent, values])

  const handleCoupon = useCallback(async (coupon) => {
    if (!plan.id) return;

    const couponPlan = { code: coupon, planId: plan.id };
    const [result, error] = await handlePromise(verifyCoupon(couponPlan));

    if (!!error) {
      // Some o desconto anterior: sem isto um cupom válido trocado por um
      // inválido continuava exibido e era enviado no pedido.
      setCouponInfo(null)
      setValidatedCoupon('')
      setFailedCoupon(coupon)
      setCouponError(error.response?.data?.message || 'Cupom inválido')
      setShowSnackbar(true)
      setSnackbarMessage(error.response?.data?.message || 'Cupom inválido')
      return false
    }

    setCouponInfo(result.data as CouponVerifyResponse);
    setValidatedCoupon(coupon);
    setFailedCoupon('')
    setCouponError('')

    setShowSnackbar(true);
    setSnackbarMessage('Desconto aplicado com sucesso!');
    return true
  }, [plan.id, plan.value]);

  const onAssignCallback = useCallback(() => {
    setTouched(objectMap(values, () => true) as any)
    validateForm()
      .then(async r => {
        const hasErrors = Object.entries(removeEmptyAttributes(r)).length > 0
        if (hasErrors) {
          console.log('has errors')
          return
        } else {
          if (!isValidCPF(values.paymentCpf.replace(/\D/g, ''))) {
            alertAndRerender('Campo inválido', 'CPF de pagamento inválido')
            return
          }
          if (values.invoiceWithCpf === 'yes' && values.invoiceCpfOption === 'other' && !isValidCPF(values.invoiceCpf.replace(/\D/g, ''))) {
            alertAndRerender('Campo inválido', 'CPF para nota fiscal inválido')
            return
          }
          if (!!onAssign) {
            // Cupom digitado e ainda não validado: tenta validar agora para não
            // perder um desconto legítimo de quem esqueceu de clicar em "Validar
            // Cupom". Se não valer, a compra segue sem desconto em vez de travar.
            const digitado = coupon.trim()
            const jaValidado = !!couponInfo && validatedCoupon === digitado
            const jaReprovado = digitado.length > 0 && failedCoupon === digitado
            const aplicavel = digitado.length === 0 || jaReprovado
              ? false
              : jaValidado || await handleCoupon(digitado)

            setModalContent(<MCAlert title="Validando informações"
                                     text="Essa operação pode demorar alguns minutos"
                                     buttonText="validando..." onClose={() => {
            }}/>)
            onAssign(values as OrderInfoState, aplicavel ? digitado : undefined)
          } else {
            setModalOpen(false)
          }
        }
      })

  }, [onAssign, setModalOpen, setModalContent, values, validateForm, setTouched, alertAndRerender, coupon, couponInfo, validatedCoupon, failedCoupon, handleCoupon])



  if (isThereAddress) {
    return <Dialog
      open={true}
      scroll="paper"
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'center',
          justifyContent: 'center',
        },
      }}
    >

      <SimpleSnackbar
        show={showSnackbar}
        message={snackbarMessage}
        onDismiss={() => setShowSnackbar(false)}
      />

      <Container
        sx={{
          ...containerStyles,
          boxSizing: 'border-box',
          width: { xs: 'calc(100vw - 32px)', sm: 400 },
          minWidth: 0,
          maxWidth: '400px !important',
          height: 'auto',
          maxHeight: 'calc(100vh - 32px)',
          overflowY: 'auto',
          margin: '0 auto',
        }}
      >
        <h2 style={h2Style}> {plan.name} </h2>
        <Divider style={{ width: '80%', margin: '10px auto', alignItems: 'center', display: 'flex' }}/>
        <Stack {...stackFlexColumn('center', 'flex-start')} spacing={.1} sx={{ maxWidth: '80%', margin: 'auto' }}>
          <StepLabel>1. Dados de pagamento</StepLabel>
          <TextField
            fullWidth
            type="text"
            label="CPF de quem está pagando"
            {...getFieldProps('paymentCpf')}
            onChange={e => setValues(current => ({
              ...current,
              paymentCpf: applyMask('cpf').onValue(e.target.value)
            }))}
            value={applyMask('cpf').onValue(values.paymentCpf)}
            error={!!errors.paymentCpf && touched.paymentCpf}
            helperText={touched.paymentCpf ? errors.paymentCpf : undefined}
            FormHelperTextProps={helperTextStyle}
            sx={{
              background: palette.primary.lighter
            }}
          />
          <Typography variant="caption" sx={{ color: palette.secondary.main, alignSelf: 'center', textAlign: 'center', mt: -0.5, mb: 0.5, lineHeight: 1.2 }}>
            Usado somente para processar o pagamento.
          </Typography>

          <StepLabel sx={{ mt: 3 }}>2. Nota fiscal</StepLabel>
          <RadioGroup
            sx={{ width: '100%' }}
            value={values.invoiceWithCpf}
            onChange={e => {
              const invoiceWithCpf = e.target.value as 'yes' | 'no'
              setValues(current => ({
                ...current,
                invoiceWithCpf,
                invoiceCpfOption: invoiceWithCpf === 'yes' ? current.invoiceCpfOption : '',
                invoiceCpf: invoiceWithCpf === 'yes' ? current.invoiceCpf : '',
              }))
            }}
          >
            <PaymentOptionCard selected={values.invoiceWithCpf === 'yes'}>
              <FormControlLabel value="yes" control={<Radio />} label="Quero nota fiscal com CPF" sx={{ width: '100%', margin: 0 }} />
            </PaymentOptionCard>
            <PaymentOptionCard selected={values.invoiceWithCpf === 'no'}>
              <FormControlLabel value="no" control={<Radio />} label="Não preciso de nota fiscal com CPF" sx={{ width: '100%', margin: 0 }} />
            </PaymentOptionCard>
          </RadioGroup>
          {!!errors.invoiceWithCpf && !!touched.invoiceWithCpf && <Typography variant="caption" sx={{ color: palette.error.main, alignSelf: 'center', mt: -0.25, mb: 0.5 }}>{errors.invoiceWithCpf}</Typography>}

          <Collapse in={values.invoiceWithCpf === 'yes'} sx={{ width: '100%' }}>
            <StepLabel>3. CPF para nota fiscal</StepLabel>
            <RadioGroup
              sx={{ width: '100%' }}
              value={values.invoiceCpfOption}
              onChange={e => setValues(current => ({
                ...current,
                invoiceCpfOption: e.target.value as 'same' | 'other',
                invoiceCpf: e.target.value === 'other' ? current.invoiceCpf : '',
              }))}
            >
              <PaymentOptionCard selected={values.invoiceCpfOption === 'same'}>
                <FormControlLabel value="same" control={<Radio />} label="Usar o mesmo CPF do pagamento" sx={{ width: '100%', margin: 0 }} />
              </PaymentOptionCard>
              <PaymentOptionCard selected={values.invoiceCpfOption === 'other'}>
                <FormControlLabel value="other" control={<Radio />} label="Informar outro CPF" sx={{ width: '100%', margin: 0 }} />
              </PaymentOptionCard>
            </RadioGroup>
            <Collapse in={values.invoiceCpfOption === 'other'}>
              <TextField
                fullWidth
                label="CPF para nota fiscal"
                {...getFieldProps('invoiceCpf')}
                onChange={e => setValues(current => ({ ...current, invoiceCpf: applyMask('cpf').onValue(e.target.value) }))}
                value={applyMask('cpf').onValue(values.invoiceCpf)}
                error={!!errors.invoiceCpf && touched.invoiceCpf}
                helperText={touched.invoiceCpf ? (errors.invoiceCpf ?? ' ') : ' '}
                FormHelperTextProps={helperTextStyle}
                sx={{ background: palette.primary.lighter }}
              />
            </Collapse>
          </Collapse>
        </Stack>

        <Stack {...stackFlexColumn('center', 'flex-start')} spacing={.1} sx={{ maxWidth: '80%', margin: '18px auto 0' }}>
          <TextField
            fullWidth
            type="text"
            label="Cupom de desconto (OPCIONAL)"
            error={!!couponError}
            helperText={couponError || undefined}
            sx={{ background: 'white', marginBottom: '10px' }}
            value={coupon}
            onChange={e => {
              setCoupon(e.target.value)
              if (couponInfo) setCouponInfo(null)
              if (couponError) setCouponError('')
            }}
          />
          <Button
            size={"large"}
            onClick={() => handleCoupon(coupon)}
            sx={{ ...btnCoupon, color: '#5D4A3C', width: '50%', }}>Validar Cupom</Button>
          <Space pixels={8} direction={'v'}/>
          <Box sx={{ width: '100%'}}>
            <CouponBox>
              <CouponText>Valor do plano:</CouponText>
              <CouponTextBold>{formatCurrency(plan.value)}</CouponTextBold>
            </CouponBox>
            <Collapse in={!!couponInfo} timeout={240} unmountOnExit>
              {couponInfo && <Box>
                  {
                    couponInfo.percentage ? <CouponBox>
                      <CouponText>Desconto aplicado:</CouponText>
                      <CouponTextBold>{couponInfo.percentage}</CouponTextBold>
                    </CouponBox> : <></>
                  }
                  {
                    couponInfo.discount ? <CouponBox>
                      <CouponText>Desconto de:</CouponText>
                      <CouponTextBold style={{color: palette.success.darker}}>{couponInfo.discount}</CouponTextBold>
                    </CouponBox> : <></>
                  }
                  {
                    couponInfo.value ? <CouponBox>
                      <CouponText>Desconto aplicado:</CouponText>
                      <CouponTextBold>{couponInfo.value}</CouponTextBold>
                    </CouponBox> : <></>
                  }

                  <Divider style={{ width: '100%', margin: '10px auto', alignItems: 'center', display: 'flex' }}/>
                  <CouponBox>
                    <CouponText sx={{ fontWeight: 'bold' }}>Total:</CouponText>
                    <CouponTextBold>{couponInfo.planValue}</CouponTextBold>
                  </CouponBox>
              </Box>}
            </Collapse>
          </Box>
        </Stack>
        <Stack
          {...(window.innerWidth < 350 ? stackFlexColumn('center', 'space-evenly') : stackFlexRow('center', 'center'))}
          style={{ width: '100%', marginBottom: '16px', marginTop: '8px' }} spacing={2}
        >
          <Button
            size={"large"}
            onClick={onCloseCallback}
            sx={btnSecondary}
            variant="contained">VOLTAR</Button>
          <Button
            size={"large"}
            onClick={onAssignCallback}
            sx={btnPrimary}
            variant="contained">CONTINUAR</Button>
        </Stack>
      </Container>
    </Dialog>
  }

  return <Container></Container>;
}

export default MCOrderInfo